// Astrea Ops Platform - Proposal data-access layer (proposals)
//
// Phase 3, ninth slice - the last big piece of Phase 3. Chose to proceed without a
// fresh AskUserQuestion (unlike the Proposal-vs-Financials scoping call) because,
// on inspection, this slice doesn't actually have a comparable architectural fork:
// the schema itself already drew the line, the same way it did for Event Form.
// `proposals`' header comment in
// supabase/migrations/20260915000004_commercial.sql says it directly - only the
// fields "genuinely stable across every project type" (status, lock state, SOW/
// retainer workflow dates and flags, the frozen rate/calc snapshots) get real
// columns; "type-specific calculator inputs" go in `calc_inputs jsonb`, because the
// Schools Assessment/Assessment/Consultation calculators aren't even built yet and
// the Event/Training calculators' ~70 input fields are still likely to change shape.
// That's the exact promoted/bridge split this file implements - 33 stable fields
// (attendeeSize, fixedFee/fixedFeeAmount, multipleWaves, mgmtReservePct, discounts/
// additions, the SOW-signed/retainer/final-invoiced workflow dates and flags,
// document_writer/document_reviewer/manager, notes) onto real columns; everything
// else - every rmDays/svDays/subDays/subRate/flight-billed-vs-actual/spotnana/
// medicalSub field across the Event and Training calculators - into `calc_inputs`,
// same live-bridge role `event_forms.planning_fields` plays for Event Form.
//
// Three things deliberately NOT built this slice, all because the app has no
// source data for them today (not judgment calls - literal absence of data, same
// category as Event Form's onsiteTeam):
//   - `payout_lines` (the S-3 fix: one row per person per payout slot, replacing
//     the positional sub1Days/sub1Rate...sub6Days/medicalSubDays/martinDays/
//     mikeDays fields) needs a real `person_id` per payout slot. The app has no
//     such link anywhere - the old-file v8.2 "*UserId link" the schema's own comment
//     mentions was never actually built into astrea-v8_9.jsx's proposal object.
//     Populating payout_lines for real needs new UI (pick a person per
//     subcontractor slot) before it can happen - a follow-up feature, not a data
//     port.
//   - `writer_id`/`finance_lead_id` (real people.id FKs) - the app's
//     writerName/pomName/adminName/financeLead are free text, the same
//     free-text-vs-FK mismatch Tasks hit with Blocked By and Event Form hit with
//     the debrief owner. They still round-trip fine via calc_inputs (swept in with
//     everything else non-promoted) rather than being dropped.
//   - `locked`/`locked_at`/`locked_by`/`rate_snapshot`/`calc_snapshot` (the S-1
//     freeze-at-signature fix) - lockProposalFigures() doesn't exist anywhere in
//     the current app. The schema built the columns ahead of that feature; there's
//     nothing to sync until it's written.
//
// Architecture: same centralized pattern as every slice since Tasks.
// ProposalTab already debounces (100ms while typing, 1500ms after a pause) through
// a single save() that calls onUpdate({...project, proposal: p, ...}) - one call
// site, same onUpdate/updateProject funnel. syncProposalChanges() is called once
// from updateProject, keyed on whether project.proposal changed by reference. No
// child tables this slice (proposal is flat, unlike Event Form's action items/
// debrief), so this is a single-row upsert exactly like financials.js.
//
// RLS: `proposals` is already in the admin/finance-only policy bucket
// (is_admin_finance(), from 20260915000007_rls_policies.sql's financial_tables
// list) - matches ProposalTab's existing canSeeFinance() UI gate, no new policy
// needed.

import { supabase } from './supabaseClient';
import { selectAllRes } from './selectAll';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
function safeDate(s) {
  return typeof s === 'string' && ISO_DATE_RE.test(s) ? s : null;
}

// [appKey, dbColumn, type] - 'n' numeric, 'b' boolean, 't' text, 'd' date.
// Everything in project.proposal NOT listed here goes into calc_inputs instead.
const PROPOSAL_COLUMNS = [
  ['status', 'status', 't'],
  ['attendeeSize', 'attendee_size', 't'],
  ['fixedFee', 'fixed_fee', 'b'],
  ['fixedFeeAmount', 'fixed_fee_amount', 'n'],
  ['multipleWaves', 'multiple_waves', 'b'],
  ['mgmtReservePct', 'mgmt_reserve_pct', 'nn'], // nullable: null = use the rate card's default for the project's year
  ['manualManagerFee', 'manual_manager_fee', 'n'],
  ['additionalLeadFee', 'additional_lead_fee', 'n'],
  ['discounts', 'discounts', 'n'],
  ['additions', 'additions', 'n'],
  ['documentWriter', 'document_writer', 't'],
  ['documentReviewer', 'document_reviewer', 't'],
  ['manager', 'manager', 't'],
  ['notes', 'notes', 't'],
  ['financeLeadNotes', 'finance_lead_notes', 't'],
  ['sowSigned', 'sow_signed', 'b'],
  ['sowSignedDate', 'sow_signed_date', 'd'],
  ['sowSignedBy', 'sow_signed_by', 't'],
  ['sowSentDate', 'sow_sent_date', 'd'],
  ['retainerInvoiced', 'retainer_invoiced', 'b'],
  ['retainerInvoicedDate', 'retainer_invoiced_date', 'd'],
  ['retainerPct', 'retainer_pct', 'n'],
  ['retainerPaid', 'retainer_paid', 'b'],
  ['actualRetainerInvoiced', 'actual_retainer_invoiced', 'n'],
  ['finalInvoiced', 'final_invoiced', 'b'],
  ['finalInvoicedDate', 'final_invoiced_date', 'd'],
  ['financeNotifiedSOW', 'finance_notified_sow', 'b'],
  ['svExpensesOnRetainer', 'sv_expenses_on_retainer', 'b'],
  ['svExpensesTotal', 'sv_expenses_total', 'n'],
  ['mvRecipient', 'mv_recipient', 't'],
  ['mvFlightCharges', 'mv_flight_charges', 'n'],
  ['mvOtherExpenses', 'mv_other_expenses', 'n'],
  ['otherNonReimbursable', 'other_non_reimbursable', 'n'],
];
const PROMOTED_KEYS = new Set(PROPOSAL_COLUMNS.map(([appKey]) => appKey));

export async function loadProposalsByProject() {
  const { data, error } = await selectAllRes(() => supabase.from('proposals').select('*').order('project_id'));
  if (error) throw error;
  return new Map((data || []).map(r => [r.project_id, r]));
}

// Falls back to appProposal untouched if there's no real proposals row yet (a
// project not touched since this slice landed). Real columns win for the promoted
// fields; calc_inputs is spread on top for everything else (real calc_inputs wins
// over whatever app_data had, same "real data wins" convention as every prior
// slice).
export function mergeProposal(appProposal, realRow) {
  const base = appProposal || {};
  if (!realRow) return base;
  const merged = { ...base };
  PROPOSAL_COLUMNS.forEach(([appKey, dbKey]) => {
    if (realRow[dbKey] !== null && realRow[dbKey] !== undefined) merged[appKey] = realRow[dbKey];
  });
  if (realRow.calc_inputs) Object.assign(merged, realRow.calc_inputs);
  return merged;
}

export async function syncProposalChanges(projectId, proposal) {
  if (!proposal) return;
  const row = { project_id: projectId };
  PROPOSAL_COLUMNS.forEach(([appKey, dbKey, type]) => {
    const v = proposal[appKey];
    if (type === 'n') row[dbKey] = v || 0;
    else if (type === 'nn') row[dbKey] = (v === null || v === undefined || v === '') ? null : (Number(v) || 0);
    else if (type === 'b') row[dbKey] = !!v;
    else if (type === 'd') row[dbKey] = safeDate(v);
    else row[dbKey] = v || (dbKey === 'status' ? 'draft' : null);
  });
  const calcInputs = {};
  Object.keys(proposal).forEach(k => {
    if (!PROMOTED_KEYS.has(k)) calcInputs[k] = proposal[k];
  });
  row.calc_inputs = calcInputs;
  const { error } = await supabase.from('proposals').upsert(row, { onConflict: 'project_id' });
  if (error) throw error;
}
